import type { PersonExperienceRole, PersonExperienceType } from "../../api";

export interface ExperienceDisplayGroup {
	employer: PersonExperienceType;
	roles: PersonExperienceRole[];
	groupDuration: number | null;
}

/**
 * Split a single employer entry into multiple visual groups when there are
 * non-contiguous employment periods (gap > 6 months between roles).
 *
 * Each group gets its own computed duration (min start to max end within group).
 */
export function splitEmployerIntoGroups(
	exp: PersonExperienceType
): ExperienceDisplayGroup[] {
	if (exp.roles.length <= 1) {
		return [
			{
				employer: exp,
				roles: exp.roles,
				groupDuration: exp.timedelta_for_employer,
			},
		];
	}

	const sortedByStart = [...exp.roles].sort((a, b) => {
		const aStart = a.start_dt ? new Date(a.start_dt).getTime() : 0;
		const bStart = b.start_dt ? new Date(b.start_dt).getTime() : 0;
		return aStart - bStart;
	});

	// Merge overlapping intervals: roles that temporally overlap belong together
	const groups: PersonExperienceRole[][] = [[sortedByStart[0]]];
	let groupEnd = sortedByStart[0].current
		? Date.now()
		: sortedByStart[0].end_dt
			? new Date(sortedByStart[0].end_dt).getTime()
			: 0;

	for (let i = 1; i < sortedByStart.length; i++) {
		const curr = sortedByStart[i];
		const currStart = curr.start_dt ? new Date(curr.start_dt).getTime() : 0;

		if (currStart <= groupEnd) {
			// Overlaps with current group
			groups[groups.length - 1].push(curr);
		} else {
			// No overlap — start new group
			groups.push([curr]);
		}
		// Extend group end to max
		const currEnd = curr.current
			? Date.now()
			: curr.end_dt
				? new Date(curr.end_dt).getTime()
				: 0;
		if (currEnd > groupEnd) groupEnd = currEnd;
	}

	return groups.reverse().map((roles) => ({
		employer: exp,
		roles,
		groupDuration: computeGroupDuration(roles),
	}));
}

function computeGroupDuration(roles: PersonExperienceRole[]): number | null {
	let minStart: number | null = null;
	let maxEnd: number | null = null;

	for (const role of roles) {
		if (role.start_dt) {
			const t = new Date(role.start_dt).getTime();
			if (minStart === null || t < minStart) minStart = t;
		}
		if (role.current) {
			maxEnd = Date.now();
		} else if (role.end_dt) {
			const t = new Date(role.end_dt).getTime();
			if (maxEnd === null || t > maxEnd) maxEnd = t;
		}
	}

	if (minStart === null || maxEnd === null) return null;

	const diffMs = maxEnd - minStart;
	return diffMs / (365.25 * 24 * 60 * 60 * 1000);
}

/** Sort display groups: current first, then by latest end_dt descending, then latest start_dt descending. */
export function sortDisplayGroups(
	groups: ExperienceDisplayGroup[]
): ExperienceDisplayGroup[] {
	return [...groups].sort((a, b) => {
		const aHasCurrent = a.roles.some((r) => r.current);
		const bHasCurrent = b.roles.some((r) => r.current);
		if (aHasCurrent !== bHasCurrent) return aHasCurrent ? -1 : 1;

		const aMaxEnd = getMaxEndTime(a.roles);
		const bMaxEnd = getMaxEndTime(b.roles);
		if (aMaxEnd !== bMaxEnd) return bMaxEnd - aMaxEnd;

		const aMaxStart = getMaxStartTime(a.roles);
		const bMaxStart = getMaxStartTime(b.roles);
		return bMaxStart - aMaxStart;
	});
}

function getMaxEndTime(roles: PersonExperienceRole[]): number {
	let max = 0;
	for (const r of roles) {
		if (r.current) return Date.now();
		if (r.end_dt) {
			const t = new Date(r.end_dt).getTime();
			if (t > max) max = t;
		}
	}
	return max;
}

function getMaxStartTime(roles: PersonExperienceRole[]): number {
	let max = 0;
	for (const r of roles) {
		if (r.start_dt) {
			const t = new Date(r.start_dt).getTime();
			if (t > max) max = t;
		}
	}
	return max;
}

/** Sort roles: current first, then by end_dt descending, then start_dt descending. */
export function sortRolesByDate(roles: PersonExperienceRole[]): PersonExperienceRole[] {
	return [...roles].sort((a, b) => {
		if (a.current !== b.current) return a.current ? -1 : 1;

		const aEnd = a.end_dt ? new Date(a.end_dt).getTime() : 0;
		const bEnd = b.end_dt ? new Date(b.end_dt).getTime() : 0;
		if (aEnd !== bEnd) return bEnd - aEnd;

		const aStart = a.start_dt ? new Date(a.start_dt).getTime() : 0;
		const bStart = b.start_dt ? new Date(b.start_dt).getTime() : 0;
		return bStart - aStart;
	});
}
